/**
 * QuoteRequestFormIA.jsx
 * =======================
 *
 * Formulaire intelligent, progressif et conditionnel pour la demande
 * de devis Coaching IA entreprise.
 *
 * API cible : POST /api/enterprise/quote-requests/ia
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import { toast } from 'sonner';
import BackButton from '../../components/BackButton';
import {
  Cpu, ArrowRight, ArrowLeft, Check, Loader2, Target,
  Users, Building2, Sparkles, Briefcase, CheckCircle2, Gift,
} from 'lucide-react';

const API = getApiUrl();

const OBJECTIVES = [
  'Leadership', 'Management', 'Communication', 'Gestion du stress',
  'Prise de parole', 'Intelligence émotionnelle', 'Gestion de conflits',
  'Performance', 'Organisation', 'Développement personnel', "Coaching d'équipe",
];

const COMPANY_SIZES = [
  { value: 'tpe', label: 'TPE', hint: 'moins de 10 salariés' },
  { value: 'pme', label: 'PME', hint: '10 à 250 salariés' },
  { value: 'eti', label: 'ETI', hint: '250 à 5 000 salariés' },
  { value: 'ge', label: 'Grand groupe', hint: '5 000+ salariés' },
];

const LEVELS = [
  { value: 'debutant', label: 'Débutant' },
  { value: 'confirme', label: 'Confirmé' },
  { value: 'expert', label: 'Expert' },
  { value: 'mixte', label: 'Populations mixtes' },
];

const NEED_TYPES = [
  { value: 'formation', label: 'Formation', hint: 'Apports structurés' },
  { value: 'coaching', label: 'Coaching', hint: 'Accompagnement individuel' },
  { value: 'hybride', label: 'Hybride', hint: 'Formation + coaching' },
];

export default function QuoteRequestFormIA() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [contact, setContact] = useState({
    company_name: '',
    company_size: '',
    vat_number: '',
    billing_address: '',
    hr_contact_name: user?.name || '',
    hr_contact_email: user?.email || '',
    hr_contact_phone: '',
  });

  const [payload, setPayload] = useState({
    collaborators_count: 25,
    objectives: [],
    current_management_level: '',
    need_type: '',
    estimated_sessions_per_collaborator_per_month: 2,
    desired_duration_months: 6,
    dedicated_onboarding_needed: null,
    custom_hr_reporting_needed: null,
    hybrid_human_sessions_ratio_pct: null,
    notes: '',
    // P0 Iter237 — Options COACHDIA Prism (optionnelles, sans prix affiché)
    insights_options: { individual_included: false, collective_included: false, notes: '' },
  });

  const showLargeOrgExtras = payload.collaborators_count >= 100;
  const showHybridRatio = payload.need_type === 'hybride';

  const toggleObjective = (o) => {
    setPayload((p) => ({
      ...p,
      objectives: p.objectives.includes(o)
        ? p.objectives.filter((x) => x !== o)
        : [...p.objectives, o],
    }));
  };

  const stepValid = useMemo(() => {
    if (step === 1) {
      return (
        contact.company_name.trim().length >= 2 &&
        contact.company_size &&
        contact.hr_contact_name.trim().length >= 2 &&
        /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.hr_contact_email)
      );
    }
    if (step === 2) {
      return (
        payload.collaborators_count > 0 &&
        payload.current_management_level &&
        payload.need_type
      );
    }
    if (step === 3) {
      return (
        payload.objectives.length >= 1 &&
        payload.estimated_sessions_per_collaborator_per_month > 0 &&
        payload.desired_duration_months > 0 &&
        (!showHybridRatio ||
          (payload.hybrid_human_sessions_ratio_pct !== null &&
            payload.hybrid_human_sessions_ratio_pct >= 0 &&
            payload.hybrid_human_sessions_ratio_pct <= 100))
      );
    }
    if (step === 4) return true;   // Étape COACHDIA Prism : toujours valide (optionnelle)
    return true;
  }, [step, contact, payload, showHybridRatio]);

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter');
      navigate('/login');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/enterprise/quote-requests/ia`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ contact, payload }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la soumission');
      }
      const data = await res.json();
      navigate(`/subscription/enterprise/quote-request/success?id=${data.id}&type=ia`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = ['Votre entreprise', 'Contexte', 'Besoin & volumétrie', 'Options COACHDIA Prism', 'Récapitulatif'];

  return (
    <div className="theme-light min-h-screen pb-28 sm:pb-12 bg-[#FBF9F3]">
      <div className="border-b border-[#E8E5DE] bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-6">
          <BackButton />
          <div className="flex items-center gap-2 mt-4 mb-2">
            <Cpu className="w-4 h-4 text-[#92710A]" />
            <p className="uppercase text-[11px] tracking-[0.25em] text-[#92710A] font-medium">
              Demande de devis — Coaching IA
            </p>
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] tracking-tight">
            Construisons votre offre sur-mesure
          </h1>
          <p className="text-[#4B5563] text-sm mt-2">
            Nos équipes reviendront vers vous sous 48&nbsp;h avec une proposition adaptée.
          </p>

          <div className="mt-6 flex items-center gap-2" data-testid="quote-request-steps">
            {steps.map((label, idx) => {
              const n = idx + 1;
              const active = n === step;
              const done = n < step;
              return (
                <div key={label} className="flex-1 flex items-center gap-2">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-medium transition-all ${
                      done ? 'bg-[#059669] text-white' : active ? 'bg-[#92710A] text-white' : 'bg-[#EBEAE3] text-[#6B7280]'
                    }`}
                  >
                    {done ? <Check className="w-3.5 h-3.5" /> : n}
                  </div>
                  <span className={`text-[11px] ${active ? 'text-[#111827] font-medium' : 'text-[#6B7280]'} hidden sm:inline`}>
                    {label}
                  </span>
                  {n < steps.length && <div className={`flex-1 h-px ${done ? 'bg-[#059669]' : 'bg-[#E8E5DE]'}`} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.2 }}
            className="space-y-5"
          >
            {step === 1 && (
              <>
                <Section title="Informations entreprise" icon={Building2}>
                  <Field label="Nom de l'entreprise *">
                    <Input data-testid="qreq-ia-company-name" value={contact.company_name}
                      onChange={(e) => setContact({ ...contact, company_name: e.target.value })}
                      placeholder="Ex. Acme Corporation"
                      className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                  </Field>
                  <div>
                    <label className="text-xs text-[#4B5563] mb-2 block">Taille de l'entreprise *</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {COMPANY_SIZES.map((s) => (
                        <button key={s.value} type="button" data-testid={`qreq-ia-size-${s.value}`}
                          onClick={() => setContact({ ...contact, company_size: s.value })}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            contact.company_size === s.value ? 'border-[#92710A] bg-[#FBF9F3]' : 'border-[#E8E5DE] bg-white hover:border-[#DDD9D0]'
                          }`}>
                          <div className="text-sm font-medium text-[#111827]">{s.label}</div>
                          <div className="text-[10px] text-[#6B7280] mt-0.5">{s.hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <Field label="Adresse de facturation">
                    <Input data-testid="qreq-ia-billing-address" value={contact.billing_address}
                      onChange={(e) => setContact({ ...contact, billing_address: e.target.value })}
                      placeholder="12 rue de la Paix, 75002 Paris"
                      className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                  </Field>
                  <Field label="N° TVA intracommunautaire">
                    <Input data-testid="qreq-ia-vat" value={contact.vat_number}
                      onChange={(e) => setContact({ ...contact, vat_number: e.target.value })}
                      placeholder="FR12345678901"
                      className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                  </Field>
                </Section>

                <Section title="Contact RH" icon={Users}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Nom du contact *">
                      <Input data-testid="qreq-ia-contact-name" value={contact.hr_contact_name}
                        onChange={(e) => setContact({ ...contact, hr_contact_name: e.target.value })}
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                    <Field label="E-mail *">
                      <Input data-testid="qreq-ia-contact-email" type="email" value={contact.hr_contact_email}
                        onChange={(e) => setContact({ ...contact, hr_contact_email: e.target.value })}
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                    <Field label="Téléphone">
                      <Input data-testid="qreq-ia-contact-phone" value={contact.hr_contact_phone}
                        onChange={(e) => setContact({ ...contact, hr_contact_phone: e.target.value })}
                        placeholder="+33 6 12 34 56 78"
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                  </div>
                </Section>
              </>
            )}

            {step === 2 && (
              <>
                <Section title="Collaborateurs à former" icon={Users}>
                  <Field label="Nombre de collaborateurs *">
                    <Input data-testid="qreq-ia-collab-count" type="number" min="1"
                      value={payload.collaborators_count}
                      onChange={(e) => setPayload({ ...payload, collaborators_count: parseInt(e.target.value) || 0 })}
                      className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                  </Field>
                </Section>

                <Section title="Niveau managérial actuel" icon={Briefcase}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {LEVELS.map((l) => (
                      <button key={l.value} type="button" data-testid={`qreq-ia-level-${l.value}`}
                        onClick={() => setPayload({ ...payload, current_management_level: l.value })}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          payload.current_management_level === l.value ? 'border-[#92710A] bg-[#FBF9F3]' : 'border-[#E8E5DE] bg-white hover:border-[#DDD9D0]'
                        }`}>
                        <div className="text-sm font-medium text-[#111827]">{l.label}</div>
                      </button>
                    ))}
                  </div>
                </Section>

                <Section title="Nature du besoin" icon={Target}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {NEED_TYPES.map((n) => (
                      <button key={n.value} type="button" data-testid={`qreq-ia-need-${n.value}`}
                        onClick={() => setPayload({ ...payload, need_type: n.value })}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          payload.need_type === n.value ? 'border-[#92710A] bg-[#FBF9F3]' : 'border-[#E8E5DE] bg-white hover:border-[#DDD9D0]'
                        }`}>
                        <div className="text-sm font-medium text-[#111827]">{n.label}</div>
                        <div className="text-[11px] text-[#6B7280] mt-0.5">{n.hint}</div>
                      </button>
                    ))}
                  </div>
                </Section>
              </>
            )}

            {step === 3 && (
              <>
                <Section title="Objectifs prioritaires (multi-choix) *" icon={Sparkles}>
                  <div className="flex flex-wrap gap-2">
                    {OBJECTIVES.map((o) => (
                      <button key={o} type="button"
                        data-testid={`qreq-ia-objective-${o.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        onClick={() => toggleObjective(o)}
                        className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                          payload.objectives.includes(o) ? 'bg-[#FBF9F3] text-[#92710A] border border-[#E8DFC8]' : 'bg-white text-[#4B5563] border border-[#E8E5DE] hover:border-[#DDD9D0]'
                        }`}>
                        {payload.objectives.includes(o) && <Check className="w-3 h-3 inline mr-1" />}
                        {o}
                      </button>
                    ))}
                  </div>
                </Section>

                <Section title="Volumétrie estimée" icon={Target}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Sessions / collaborateur / mois *">
                      <Input data-testid="qreq-ia-sessions-per-collab" type="number" min="1"
                        value={payload.estimated_sessions_per_collaborator_per_month}
                        onChange={(e) => setPayload({ ...payload, estimated_sessions_per_collaborator_per_month: parseInt(e.target.value) || 0 })}
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                    <Field label="Durée souhaitée (mois) *">
                      <Input data-testid="qreq-ia-duration" type="number" min="1"
                        value={payload.desired_duration_months}
                        onChange={(e) => setPayload({ ...payload, desired_duration_months: parseInt(e.target.value) || 0 })}
                        className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl" />
                    </Field>
                  </div>

                  {showHybridRatio && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                      className="mt-4 p-4 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8]"
                      data-testid="qreq-ia-hybrid-ratio-block">
                      <p className="text-xs text-[#92710A] font-medium uppercase tracking-wider mb-2">
                        Besoin hybride détecté
                      </p>
                      <Field label="Part de sessions humaines souhaitée (%)">
                        <Input data-testid="qreq-ia-hybrid-ratio" type="number" min="0" max="100"
                          value={payload.hybrid_human_sessions_ratio_pct ?? ''}
                          onChange={(e) => setPayload({ ...payload, hybrid_human_sessions_ratio_pct: e.target.value === '' ? null : parseInt(e.target.value) })}
                          className="bg-white border-[#DDD9D0] rounded-xl" />
                      </Field>
                    </motion.div>
                  )}
                </Section>

                {showLargeOrgExtras && (
                  <Section title="Questions complémentaires — grande organisation" icon={Building2}>
                    <div className="space-y-3" data-testid="qreq-ia-large-org-extras">
                      <YesNoField testid="qreq-ia-onboarding"
                        label="Souhaitez-vous un onboarding dédié de vos managers ?"
                        value={payload.dedicated_onboarding_needed}
                        onChange={(v) => setPayload({ ...payload, dedicated_onboarding_needed: v })} />
                      <YesNoField testid="qreq-ia-reporting"
                        label="Avez-vous besoin d'un reporting RH personnalisé ?"
                        value={payload.custom_hr_reporting_needed}
                        onChange={(v) => setPayload({ ...payload, custom_hr_reporting_needed: v })} />
                    </div>
                  </Section>
                )}

                <Section title="Notes complémentaires" icon={Sparkles}>
                  <Textarea data-testid="qreq-ia-notes" value={payload.notes}
                    onChange={(e) => setPayload({ ...payload, notes: e.target.value })}
                    placeholder="Contexte, contraintes, attentes particulières..."
                    className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl min-h-[100px]" />
                </Section>
              </>
            )}

            {step === 4 && (
              <Section title="Options COACHDIA Prism (optionnel)" icon={Gift}>
                <p className="text-xs text-[#6B7280] leading-relaxed">
                  Enrichissez votre coaching IA avec nos modules Insights. L'admin CoachDIA vous enverra le chiffrage détaillé avec le devis final (tarification existante, aucun coût caché).
                </p>
                <div className="grid grid-cols-1 gap-3 mt-2">
                  <OptionToggle
                    testid="qreq-ia-insights-individual"
                    active={payload.insights_options.individual_included}
                    onClick={() => setPayload((p) => ({
                      ...p,
                      insights_options: {
                        ...p.insights_options,
                        individual_included: !p.insights_options.individual_included,
                      },
                    }))}
                    title="COACHDIA Prism pour chaque collaborateur"
                    description="Chaque collaborateur inclus bénéficie d'un test comportemental premium sans paiement personnel. Rapport PDF, profil d'énergies, plan 30/60/90 jours."
                  />
                  <OptionToggle
                    testid="qreq-ia-insights-collective"
                    active={payload.insights_options.collective_included}
                    onClick={() => setPayload((p) => ({
                      ...p,
                      insights_options: {
                        ...p.insights_options,
                        collective_included: !p.insights_options.collective_included,
                      },
                    }))}
                    title="COACHDIA Pulse / COACHDIA Pulse"
                    description="1 analyse d'équipe avec cartographie, synergies, frictions, scripts managériaux. Lancement dès 3 profils individuels complétés."
                  />
                </div>
                <div className="mt-3">
                  <label className="text-xs text-[#4B5563] mb-1.5 block font-medium">Notes COACHDIA Prism (optionnel)</label>
                  <Textarea
                    data-testid="qreq-ia-insights-notes"
                    value={payload.insights_options.notes || ''}
                    onChange={(e) => setPayload((p) => ({
                      ...p,
                      insights_options: { ...p.insights_options, notes: e.target.value },
                    }))}
                    placeholder="Précisez un besoin COACHDIA Prism spécifique..."
                    className="bg-[#F8F7F4] border-[#DDD9D0] rounded-xl min-h-[80px]"
                  />
                </div>
              </Section>
            )}

            {step === 5 && (
              <Section title="Récapitulatif avant envoi" icon={CheckCircle2}>
                <SummaryRow label="Entreprise" value={contact.company_name} />
                <SummaryRow label="Taille" value={COMPANY_SIZES.find(s => s.value === contact.company_size)?.label} />
                <SummaryRow label="Contact RH" value={`${contact.hr_contact_name} · ${contact.hr_contact_email}`} />
                <SummaryRow label="Collaborateurs" value={`${payload.collaborators_count}`} />
                <SummaryRow label="Niveau" value={LEVELS.find(l => l.value === payload.current_management_level)?.label} />
                <SummaryRow label="Besoin" value={NEED_TYPES.find(n => n.value === payload.need_type)?.label} />
                <SummaryRow label="Objectifs" value={payload.objectives.join(', ') || '—'} />
                <SummaryRow label="Volumétrie"
                  value={`${payload.estimated_sessions_per_collaborator_per_month} sess./collab./mois × ${payload.desired_duration_months} mois`} />
                {showHybridRatio && payload.hybrid_human_sessions_ratio_pct !== null && (
                  <SummaryRow label="Part humaine" value={`${payload.hybrid_human_sessions_ratio_pct} %`} />
                )}
                {showLargeOrgExtras && (
                  <>
                    <SummaryRow label="Onboarding dédié" value={boolLabel(payload.dedicated_onboarding_needed)} />
                    <SummaryRow label="Reporting RH custom" value={boolLabel(payload.custom_hr_reporting_needed)} />
                  </>
                )}
                <SummaryRow
                  label="COACHDIA Prism"
                  value={
                    (payload.insights_options.individual_included || payload.insights_options.collective_included)
                      ? [
                          payload.insights_options.individual_included ? 'Individuel' : null,
                          payload.insights_options.collective_included ? 'Collectif' : null,
                        ].filter(Boolean).join(' + ')
                      : 'Aucun'
                  }
                />
                {payload.notes && <SummaryRow label="Notes" value={payload.notes} />}
                <p className="text-xs text-[#6B7280] mt-4 leading-relaxed">
                  En soumettant cette demande, vous autorisez nos équipes commerciales à vous contacter
                  à l'adresse <strong>{contact.hr_contact_email}</strong> sous 48&nbsp;h ouvrées.
                </p>
              </Section>
            )}

            <div className="flex items-center justify-between pt-4">
              <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
                data-testid="qreq-ia-prev"
                className="px-5 py-2.5 rounded-full border border-[#E8E5DE] bg-white text-[#4B5563] text-sm font-medium hover:bg-[#F8F7F4] disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Précédent
              </button>
              {step < 5 ? (
                <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!stepValid}
                  data-testid="qreq-ia-next"
                  className="px-5 py-2.5 rounded-full bg-[#111827] text-white text-sm font-medium hover:bg-[#1f2937] disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-2">
                  Suivant <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={submitting}
                  data-testid="qreq-ia-submit"
                  className="px-6 py-2.5 rounded-full bg-[#D4AF37] text-[#111827] text-sm font-display font-semibold hover:bg-[#E5C15A] disabled:opacity-50 transition-all inline-flex items-center gap-2">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Envoyer ma demande de devis
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#E8E5DE] rounded-2xl shadow-sm p-5">
      <h3 className="text-[#111827] font-display font-medium mb-4 flex items-center gap-2 text-sm">
        {Icon && <Icon className="w-4 h-4 text-[#92710A]" />}
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[#4B5563] mb-1.5 block font-medium">{label}</label>
      {children}
    </div>
  );
}
function YesNoField({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="text-sm text-[#4B5563]">{label}</span>
      <div className="flex gap-2">
        {[{ v: true, l: 'Oui' }, { v: false, l: 'Non' }].map((opt) => (
          <button key={opt.l} type="button"
            data-testid={`${testid}-${opt.l.toLowerCase()}`}
            onClick={() => onChange(opt.v)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              value === opt.v ? 'bg-[#111827] text-white' : 'bg-white border border-[#E8E5DE] text-[#4B5563]'
            }`}>
            {opt.l}
          </button>
        ))}
      </div>
    </div>
  );
}
function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[#F0EEE8] last:border-0">
      <span className="text-xs text-[#6B7280] uppercase tracking-wider">{label}</span>
      <span className="text-sm text-[#111827] text-right max-w-[60%]">{value || '—'}</span>
    </div>
  );
}
function boolLabel(v) {
  if (v === true) return 'Oui';
  if (v === false) return 'Non';
  return '—';
}

function OptionToggle({ active, onClick, title, description, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-pressed={active}
      className={`text-left p-4 rounded-2xl border transition-all ${
        active
          ? 'border-[#D4AF37] bg-[#FFFBEB] ring-1 ring-[#D4AF37]/30'
          : 'border-[#E8E5DE] bg-white hover:border-[#D4AF37]/40'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-5 h-5 shrink-0 mt-0.5 rounded-md border-2 flex items-center justify-center ${
          active ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-[#D1D5DB] bg-white'
        }`}>
          {active && <Check className="w-3 h-3 text-white" />}
        </div>
        <div className="flex-1">
          <div className="text-sm font-display font-medium text-[#111827]">{title}</div>
          <div className="text-xs text-[#4B5563] mt-1 leading-relaxed">{description}</div>
        </div>
      </div>
    </button>
  );
}
